import { Camera, Check, FilePenLine, Minus, Plus, Ruler, Scale } from 'lucide-react';
import { useI18n } from '../lib/i18n';
import { itemKey, legacyItemKey, estadoEfectivo, normalizarReemplazo, resumenReemplazo, type PropuestaItem, type PropuestaSoltada, type ResumenExacto } from '../lib/negotiationKeys';
import type { NegData, NegDiff } from './NegotiationPanel';

const money = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;
const qtyTxt = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(2));

/**
 * EL RESUMEN QUE LEE SUPPLEMENTING: qué estamos pidiendo distinto vs el estimado del seguro y qué soltamos.
 *
 * Desde el 2026-10-09 sale de la PROPUESTA EXACTA (`summary.exact`, backend supplements/propuesta.ts): los mismos
 * números que el estimado generado —el counter offer que viajó adjunto— partida por partida, con el porqué de cada
 * una (las razones de la comparación, la nota, los argumentos elegidos, el cambio de ítem, las fotos). Pedido del
 * dueño: "quiero que eso sea bien detallado… bastante exacto, con el porqué, para ayudar al supplementer a pelearlo".
 * Lo analizado antes de esa fecha no tiene `exact`: se pinta como siempre (los tres bloques de abajo).
 */
function Bucket({ icon, title, right, children }: { icon: React.ReactNode; title: string; right?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 10, padding: 10, background: 'var(--bg)' }}>
      <div style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
        {icon} {title}<span style={{ flex: 1 }} />{right}
      </div>
      {children}
    </div>
  );
}

/** Links a fotos (CompanyCam / Drive) de una partida, como chips. */
function Fotos({ links, t }: { links: string[]; t: (k: string) => string }) {
  if (!links.length) return null;
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {links.map((u, j) => (
        <a key={j} href={u} target="_blank" rel="noreferrer" className="badge gray" style={{ fontSize: 'var(--fs-2xs)', display: 'inline-flex', alignItems: 'center', gap: 3, textDecoration: 'none' }}>
          <Camera size={10} strokeWidth={2} /> {t('rs.photo')} {j + 1}
        </a>
      ))}
    </div>
  );
}

/** Una partida pedida, con sus números efectivos y su porqué. */
function Pedida({ it, t }: { it: PropuestaItem; t: (k: string, v?: Record<string, string>) => string }) {
  const chips: Array<{ text: string; color: string }> = [];
  if (it.kind === 'pending') chips.push({ text: t('rs.kindPending'), color: 'var(--orange)' });
  if (it.kind === 'invoice') chips.push({ text: t('rs.kindInvoice'), color: 'var(--accent)' });
  if (it.kind === 'added') chips.push({ text: t('rs.kindAdded'), color: 'var(--accent)' });
  if (it.quantity_disputed && it.their_qty != null && it.qty != null) chips.push({ text: t('rs.qtyChange', { a: qtyTxt(it.their_qty), b: qtyTxt(it.qty), u: it.unit ?? '' }), color: 'var(--ink)' });
  if (!it.price_confirmed) chips.push({ text: t('rs.priceToConfirm'), color: 'var(--orange)' });
  const why = [...it.why.reasons];
  const sinPorque = !why.length && !it.why.note && !it.why.rebuttals.length && !it.why.replace_with && !it.why.measurement_note && !it.urls.length && !it.photos;
  return (
    <li style={{ display: 'grid', gap: 2 }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <span>{it.n != null ? <span className="muted" style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)' }}>#{it.n} </span> : null}<strong>{it.name}</strong></span>
        {it.original_name && <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>({t('rs.was', { x: it.original_name })})</span>}
        <span style={{ flex: 1 }} />
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', whiteSpace: 'nowrap' }}>
          <span style={{ color: 'var(--green)', fontWeight: 700 }}>{money(it.our_amount)}</span> <span className="muted">vs</span> <span style={{ color: 'var(--red)' }}>{money(it.their_amount)}</span>
          {it.gap > 0 && <span style={{ marginLeft: 6, fontWeight: 700, color: 'var(--green)' }}>+{money(it.gap)}</span>}
        </span>
      </div>
      {chips.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {chips.map((c, i) => <span key={i} style={{ fontSize: 'var(--fs-2xs)', fontWeight: 700, color: c.color }}>{c.text}</span>)}
        </div>
      )}
      {it.why.replace_with && <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--orange)', fontWeight: 700 }}>{t('rs.replaceWith', { x: it.why.replace_with })}</div>}
      {sinPorque
        ? <div className="muted" style={{ fontSize: 'var(--fs-2xs)', fontStyle: 'italic' }}>{t('rs.noNoteYet')}</div>
        : (
          <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--muted)', lineHeight: 1.5, display: 'grid', gap: 1 }}>
            {why.map((r, i) => <div key={i}>• {r}</div>)}
            {it.why.measurement_note && <div>• {it.why.measurement_note}</div>}
            {it.why.note && <div style={{ color: 'var(--ink)' }}>{it.why.note}</div>}
            {it.why.rebuttals.map((r, i) => <div key={`r${i}`} style={{ color: 'var(--accent)' }}>↳ {r}</div>)}
            {it.photos > 0 && <div>{t('rs.photosUploaded', { n: String(it.photos) })}</div>}
            <Fotos links={it.urls} t={t} />
          </div>
        )}
    </li>
  );
}

function Soltada({ l, t }: { l: PropuestaSoltada; t: (k: string, v?: Record<string, string>) => string }) {
  return (
    <li style={{ display: 'grid', gap: 1 }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <span>{l.n != null ? <span className="muted" style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)' }}>#{l.n} </span> : null}{l.name}</span>
        <span style={{ flex: 1 }} />
        <span className="muted" style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', whiteSpace: 'nowrap' }}>
          {l.action === 'remove' ? t('rs.notPaid') : t('rs.weHad', { a: money(l.our_original_amount ?? 0), b: money(l.revised_amount) })}
          {l.conceded > 0 && <span style={{ marginLeft: 6, color: 'var(--red)' }}>−{money(l.conceded)}</span>}
        </span>
      </div>
      {(l.why.length > 0 || l.note) && (
        <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{[l.why.join(' · '), l.note].filter(Boolean).join(' — ')}</div>
      )}
    </li>
  );
}

function ResumenExactoView({ neg, ex, t }: { neg: NegData; ex: ResumenExacto; t: (k: string, v?: Record<string, string>) => string }) {
  const gen = neg.summary.generated ?? null;
  const pld = neg.analysis.price_list_dispute;
  const asking = ex.items.reduce((s, i) => s + i.gap, 0);
  const cell = (label: string, value: string, color: string, sub?: string) => (
    <div style={{ minWidth: 110 }}>
      <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{label}</div>
      <div style={{ fontWeight: 700, fontFamily: 'var(--font-mono)', color }}>{value}</div>
      {sub && <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{sub}</div>}
    </div>
  );
  return (
    <>
      {/* Los números: lo del seguro, lo que pedimos encima, y el counter offer exacto (el mismo del estimado generado). */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', border: '1px solid var(--line)', borderRadius: 10, padding: 10, background: 'var(--bg)', alignItems: 'flex-start' }}>
        {ex.their_total_cents != null && cell(t('rs.theirOffer'), money(ex.their_total_cents / 100), 'var(--red)', ex.conceded_cents > 0 ? t('rs.concededSub', { x: money(ex.conceded_cents / 100) }) : undefined)}
        {cell(t('rs.asking'), `+${money(asking)}`, 'var(--green)', t('rs.askingSub', { n: String(ex.items.length) }))}
        {cell(`${t('rs.counterOffer')} · ${ex.source === 'estimate' ? t('rs.exact') : t('rs.approx')}`, money(ex.counter_rcv_cents / 100), 'var(--accent)',
          gen ? t('rs.generatedEstimate', { name: gen.tag ?? gen.name, rcv: money(gen.rcv_cents / 100) }) : undefined)}
        {gen && Math.abs(gen.rcv_cents - ex.counter_rcv_cents) >= 100 && (
          <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--orange)', fontWeight: 700, alignSelf: 'center' }}>{t('rs.generatedStale')}</div>
        )}
      </div>
      {ex.source === 'sum' && <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--orange)' }}>{t('rs.approxNote')}</div>}
      {pld && (
        <Bucket icon={<Scale size={13} strokeWidth={1.75} />} title={t('rs.priceListUpdate')}>
          <div style={{ fontSize: 'var(--fs-sm)' }}>{t('rs.priceListUpdateText', { pl: pld.their_price_list })}</div>
          <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginTop: 2 }}>{pld.our_price_list} → {pld.their_price_list}{pld.estimated_impact ? ` · ~${money(pld.estimated_impact)}` : ''}</div>
        </Bucket>
      )}
      {ex.items.length > 0 ? (
        <Bucket icon={<Plus size={13} strokeWidth={1.75} />} title={t('rs.askingItems', { n: String(ex.items.length) })} right={<span style={{ fontFamily: 'var(--font-mono)', color: 'var(--green)', fontSize: 'var(--fs-2xs)' }}>+{money(asking)}</span>}>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 'var(--fs-sm)', display: 'grid', gap: 6 }}>
            {ex.items.map((it, i) => <Pedida key={it.key ?? `x${i}`} it={it} t={t} />)}
          </ul>
        </Bucket>
      ) : <div className="muted" style={{ fontSize: 'var(--fs-sm)' }}>{t('rs.nothingAsked')}</div>}
      {ex.letting_go.length > 0 && (
        <Bucket icon={<Minus size={13} strokeWidth={1.75} />} title={t('rs.lettingGoItems', { n: String(ex.letting_go.length) })} right={<span style={{ fontFamily: 'var(--font-mono)', color: 'var(--red)', fontSize: 'var(--fs-2xs)' }}>−{money(ex.conceded_cents / 100)}</span>}>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 'var(--fs-sm)', display: 'grid', gap: 4 }}>
            {ex.letting_go.map((l, i) => <Soltada key={l.key ?? `g${i}`} l={l} t={t} />)}
          </ul>
        </Bucket>
      )}
      {ex.warnings.length > 0 && ex.source === 'estimate' && (
        <div className="muted" style={{ fontSize: 'var(--fs-2xs)', display: 'grid', gap: 1 }}>{ex.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}</div>
      )}
      <div className="muted" style={{ fontSize: 'var(--fs-2xs)', display: 'flex', alignItems: 'center', gap: 4 }}><Check size={11} strokeWidth={2} /> {t('rs.exactFooter')}</div>
    </>
  );
}

export function SupplementSummary({ neg, hideTitle }: { neg: NegData; hideTitle?: boolean }) {
  const { t } = useI18n();
  const a = neg.analysis;
  const exact = neg.summary?.exact ?? null;
  /**
   * LA NOTA Y LAS FOTOS DE CADA PARTIDA (pedido del dueño 2026-08-24: "en what we are asking
   * different quiero ver las notas y el link de fotos"). Las claves salen de `itemKey` del panel, importado y no
   * re-escrito: dos definiciones de la misma clave serían dos pantallas mostrando notas distintas de la misma partida.
   */
  const notas = neg.notes ?? {};
  const fotos = neg.urls ?? {};
  const notaDe = (d: NegDiff) => notas[itemKey(d)] ?? notas[legacyItemKey(d.description)] ?? null;
  const fotosDe = (d: NegDiff) => fotos[itemKey(d)] ?? fotos[legacyItemKey(d.description)] ?? [];
  const rebuttals = neg.rebuttals ?? {};
  const rebDe = (d: NegDiff) => rebuttals[itemKey(d)] ?? null;
  const detalle = (d: NegDiff) => {
    const nota = notaDe(d);
    const links = fotosDe(d);
    const reb = rebDe(d);
    const nReb = (reb?.selected.length ?? 0) + (reb?.suggested.length ?? 0) + (reb?.custom ? 1 : 0);
    // SIN NOTA SE DICE QUE NO HAY (Johney Aazad: 0 notas, 0 fotos; la falta era indistinguible de un error de pantalla).
    const reemplazo = resumenReemplazo(normalizarReemplazo((neg.replacements ?? {})[itemKey(d)]), d.unit);
    if (!nota && links.length === 0 && nReb === 0 && !reemplazo) {
      return <div className="muted" style={{ fontSize: 'var(--fs-2xs)', fontStyle: 'italic', marginTop: 2 }}>{t('rs.noNoteYet')}</div>;
    }
    return (
      <div style={{ display: 'grid', gap: 2, marginTop: 2 }}>
        {reemplazo && <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--orange)', fontWeight: 700 }}>{t('rs.replaceWith', { x: reemplazo })}</div>}
        {nota && <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--muted)', lineHeight: 1.5 }}>{nota}</div>}
        {nReb > 0 && <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--accent)' }}>{t('rs.rebuttalsChosen', { n: String(nReb) })}{reb?.custom ? ` · ${reb.custom}` : ''}</div>}
        <Fotos links={links} t={t} />
      </div>
    );
  };
  /**
   * ESTE RESUMEN ES LO QUE ESTAMOS PIDIENDO, NO TODO LO QUE LA IA ENCONTRÓ (dueño 2026-08-24, Johney Aazad): las
   * soltadas no se listan como pedidas. El estado sale de `estadoEfectivo`, la MISMA función del panel.
   */
  const decisiones = neg.decisions ?? {};
  const diffs = (a.diffs ?? [])
    .map((d) => ({ ...d, reasons: Array.isArray(d.reasons) ? d.reasons : [] }))
    .filter((d) => estadoEfectivo(d, decisiones) !== 'accepted');
  const missing = diffs.filter((d) => d.missing_from_theirs);
  const medidasDelSeguro = neg.measurement_status === 'theirs';
  const measurement = medidasDelSeguro ? [] : diffs.filter((d) => !d.missing_from_theirs && d.reasons.some((r) => r.type === 'quantity'));
  const pld = a.price_list_dispute;
  const nothing = !pld && missing.length === 0 && measurement.length === 0;

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {!hideTitle && (
        <div style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
          <FilePenLine size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('rs.summaryTitle')}
        </div>
      )}
      {exact ? <ResumenExactoView neg={neg} ex={exact} t={t} /> : (
        <>
          {pld && (
            <Bucket icon={<Scale size={13} strokeWidth={1.75} />} title={t('rs.priceListUpdate')}>
              <div style={{ fontSize: 'var(--fs-sm)' }}>{t('rs.priceListUpdateText', { pl: pld.their_price_list })}</div>
              <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginTop: 2 }}>
                {pld.our_price_list} → {pld.their_price_list}{pld.estimated_impact ? ` · ~${money(pld.estimated_impact)}` : ''}
              </div>
            </Bucket>
          )}
          {missing.length > 0 && (
            <Bucket icon={<Plus size={13} strokeWidth={1.75} />} title={`${t('rs.missingItems')} (${missing.length})`}>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 'var(--fs-sm)', display: 'grid', gap: 2 }}>
                {missing.map((d, i) => (
                  <li key={i}>{d.description}{d.gap ? <span className="muted"> — {money(d.gap)}</span> : null}
                    {detalle(d)}
                  </li>
                ))}
              </ul>
            </Bucket>
          )}
          {measurement.length > 0 && (
            <Bucket icon={<Ruler size={13} strokeWidth={1.75} />} title={`${t('rs.wrongMeasurement')} (${measurement.length})`}>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 'var(--fs-sm)', display: 'grid', gap: 2 }}>
                {measurement.map((d, i) => (
                  <li key={i}>{d.description}
                    {d.our_qty != null && d.their_qty != null ? <span className="muted"> — {d.their_qty} → {d.our_qty} {d.unit ?? ''}</span> : null}
                    {detalle(d)}
                  </li>
                ))}
              </ul>
            </Bucket>
          )}
          {nothing && <div className="muted" style={{ fontSize: 'var(--fs-sm)' }}>{t('rs.noDiffs')}</div>}
        </>
      )}
    </div>
  );
}
